import React, { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    RadioGroup,
    FormControlLabel,
    Radio,
    Box,
    Alert,
    Chip,
} from '@mui/material';
import {
    ContentCopy as CopyIcon,
    Link as LinkIcon,
} from '@mui/icons-material';
import { useTranslate } from 'react-admin';
import { LibraryItem, LibraryReferenceMode } from '../../services/libraryService';

interface InsertFromLibraryDialogProps {
    open: boolean;
    onClose: () => void;
    item: LibraryItem;
    onConfirm: (mode: LibraryReferenceMode) => void;
}

const InsertFromLibraryDialog: React.FC<InsertFromLibraryDialogProps> = ({
    open,
    onClose,
    item,
    onConfirm,
}) => {
    const [mode, setMode] = useState<LibraryReferenceMode>('copy');
    const translate = useTranslate();

    const canCopy = item.access.canCopy;

    // If can't copy, force reference mode
    React.useEffect(() => {
        if (!canCopy) {
            setMode('reference');
        }
    }, [canCopy]);

    const handleConfirm = () => {
        onConfirm(mode);
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>
                {translate('library.insertDialog.title')}
            </DialogTitle>
            <DialogContent>
                <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle1" fontWeight="bold">
                        {item.title}
                    </Typography>
                    <Chip
                        label={item.itemType === 'course'
                            ? translate('library.types.course')
                            : translate('library.types.lesson')}
                        size="small"
                        color={item.itemType === 'course' ? 'primary' : 'secondary'}
                        sx={{ mt: 1 }}
                    />
                </Box>

                {!canCopy && (
                    <Alert severity="info" sx={{ mb: 2 }}>
                        {translate('library.insertDialog.copyNotAllowed')}
                    </Alert>
                )}

                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {translate('library.insertDialog.description')}
                </Typography>

                <RadioGroup
                    value={mode}
                    onChange={(e) => setMode(e.target.value as LibraryReferenceMode)}
                >
                    <FormControlLabel
                        value="copy"
                        control={<Radio />}
                        disabled={!canCopy}
                        label={
                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                                <CopyIcon color={canCopy ? 'primary' : 'disabled'} sx={{ mt: 0.5 }} />
                                <Box>
                                    <Typography variant="body1" fontWeight="medium">
                                        {translate('library.insertDialog.copyOption')}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        {translate('library.insertDialog.copyDescription')}
                                    </Typography>
                                </Box>
                            </Box>
                        }
                        sx={{
                            alignItems: 'flex-start',
                            mb: 2,
                            p: 1,
                            border: 1,
                            borderColor: mode === 'copy' ? 'primary.main' : 'divider',
                            borderRadius: 1,
                            opacity: canCopy ? 1 : 0.5,
                        }}
                    />

                    <FormControlLabel
                        value="reference"
                        control={<Radio />}
                        label={
                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                                <LinkIcon color="secondary" sx={{ mt: 0.5 }} />
                                <Box>
                                    <Typography variant="body1" fontWeight="medium">
                                        {translate('library.insertDialog.referenceOption')}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        {translate('library.insertDialog.referenceDescription')}
                                    </Typography>
                                </Box>
                            </Box>
                        }
                        sx={{
                            alignItems: 'flex-start',
                            p: 1,
                            border: 1,
                            borderColor: mode === 'reference' ? 'secondary.main' : 'divider',
                            borderRadius: 1,
                        }}
                    />
                </RadioGroup>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>
                    {translate('ra.action.cancel')}
                </Button>
                <Button onClick={handleConfirm} variant="contained">
                    {translate('library.insertDialog.confirm')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default InsertFromLibraryDialog;
